import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  AdminUserDevice,
  AdminUserRecentMatch,
  AdminUserTransaction,
  AdminUserTransactionsResponse,
  AuditLogEntry,
  UserDetailResponse,
} from '@shared/types';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import CurrencyModal from '../../components/CurrencyModal';
import Pill from '../../components/Pill';
import Card from '../../components/Card';
import Alert from '../../components/Alert';
import PageHeader from '../../components/PageHeader';
import PageSection from '../../components/PageSection';
import DataRow from '../../components/DataRow';
import ReasonedConfirmModal from '../../components/ReasonedConfirmModal';
import {
  banUser,
  getUser,
  getUserAudit,
  listUserTransactions,
  unbanUser,
  type AuditListResponse,
} from '../../services/users';
import { ApiError } from '../../services/api';
import { useToast } from '../../stores/toastStore';
import { formatDateTime } from '../../utils/date';
import { BannedPill, OnlinePill } from './pills';

/**
 * Admin user detail screen. Loads the user + a short audit preview on mount.
 * Provides:
 *   - ban / unban (with confirm modal; reason required on ban)
 *   - currency adjustment via `<CurrencyModal />`
 *
 * All destructive actions route through `<ConfirmModal />` and refetch the
 * user on success so balances / status reflect the server truth immediately.
 */
export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();

  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; user: UserDetailResponse }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  const [audit, setAudit] = useState<AuditListResponse | null>(null);
  // `transactions.page` is the source of truth for current page — no extra
  // state needed. Callers pass the explicit page to `fetchTransactions`.
  const [transactions, setTransactions] =
    useState<AdminUserTransactionsResponse | null>(null);

  const [confirmKind, setConfirmKind] = useState<
    'ban' | 'unban' | null
  >(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const [currencyOpen, setCurrencyOpen] = useState(false);

  const fetchUser = useCallback(async () => {
    if (!id) {
      setState({ status: 'error', message: 'Missing user id.' });
      return;
    }
    setState({ status: 'loading' });
    try {
      const data = await getUser(id);
      setState({ status: 'ready', user: data });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load user';
      setState({ status: 'error', message });
    }
  }, [id]);

  const fetchTransactions = useCallback(
    async (pageArg: number) => {
      if (!id) return;
      try {
        const data = await listUserTransactions(id, {
          page: pageArg,
          limit: 20,
        });
        setTransactions(data);
      } catch {
        // Transactions failure is non-blocking — the main detail still renders.
        setTransactions(null);
      }
    },
    // Deliberately NOT depending on `transactionsPage`: callers pass
    // `pageArg` directly, and including the state would re-create the
    // callback every page change, firing the mount effect again and
    // bouncing us back to page 1.
    [id],
  );

  const fetchAudit = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getUserAudit(id, 5);
      setAudit(data);
    } catch {
      // Audit preview failure is non-blocking — the main detail view still
      // renders. A dedicated audit screen (planned elsewhere) will surface
      // the full log.
      setAudit(null);
    }
  }, [id]);

  useEffect(() => {
    void fetchUser();
    void fetchAudit();
    void fetchTransactions(1);
  }, [fetchUser, fetchAudit, fetchTransactions]);

  function openBanConfirm() {
    setConfirmKind('ban');
  }

  function openUnbanConfirm() {
    setConfirmKind('unban');
  }

  function closeConfirm() {
    if (confirmBusy) return;
    setConfirmKind(null);
  }

  async function handleBanOrUnban(reason: string) {
    if (!id || !confirmKind) return;
    const verb = confirmKind === 'ban' ? banUser : unbanUser;
    const successMsg =
      confirmKind === 'ban' ? 'User banned' : 'User unbanned';
    const errFallback =
      confirmKind === 'ban' ? 'Failed to ban user' : 'Failed to unban user';

    setConfirmBusy(true);
    try {
      await verb(id, reason);
      showToast(successMsg, 'success');
      setConfirmKind(null);
      await Promise.all([fetchUser(), fetchAudit()]);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.error}: ${err.message}`
          : err instanceof Error
            ? err.message
            : errFallback;
      showToast(message, 'error');
    } finally {
      setConfirmBusy(false);
    }
  }

  async function handleCurrencySuccess() {
    await Promise.all([fetchUser(), fetchAudit(), fetchTransactions(1)]);
  }

  async function handleTransactionsPageChange(next: number) {
    await fetchTransactions(next);
  }

  if (state.status === 'loading') {
    return (
      <div data-testid="user-detail-loading" className="text-sm text-slate-500">
        Loading user…
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="space-y-4" data-testid="user-detail-error">
        <Link
          to="/users"
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Back to Users
        </Link>
        <Alert>{state.message}</Alert>
      </div>
    );
  }

  const user = state.user;
  const avatarLetter = (user.displayName || user.username || '?')
    .charAt(0)
    .toUpperCase();

  const banButton = user.isBanned ? (
    <button
      type="button"
      onClick={openUnbanConfirm}
      data-testid="user-detail-unban-button"
      className="text-sm rounded px-3 py-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
    >
      Unban
    </button>
  ) : (
    <button
      type="button"
      onClick={openBanConfirm}
      data-testid="user-detail-ban-button"
      className="text-sm rounded px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50"
    >
      Ban
    </button>
  );

  return (
    <div className="space-y-6" data-testid="user-detail-screen">
      <PageHeader
        back={{
          to: '/users',
          label: 'Back to Users',
          testId: 'user-detail-back',
        }}
        title={
          <span className="flex items-center gap-3 min-w-0">
            <span
              aria-hidden="true"
              className="w-10 h-10 rounded-full bg-slate-800 text-white flex items-center justify-center text-base font-bold shrink-0"
              data-testid="user-detail-avatar"
            >
              {avatarLetter}
            </span>
            <span
              className="truncate"
              data-testid="user-detail-display-name"
            >
              {user.displayName}
            </span>
          </span>
        }
        badges={
          <>
            {/* Live Redis presence, not the `lastActiveAt` mirror below —
                the two disagree routinely and this is the one that answers
                "are they connected right now". */}
            <OnlinePill isOnline={user.isOnline} />
            <BannedPill isBanned={user.isBanned} />
            {user.isBot && <Pill variant="accent">Bot</Pill>}
          </>
        }
        subtitle={<>@{user.username}</>}
        actions={banButton}
      />

      {/* Profile + balances side-by-side ------------------------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PageSection
          title="Profile"
          bodySpacing="tight"
          testId="user-detail-profile"
        >
          <DataRow label="User ID" value={user.id} testId="user-detail-id" />
          <DataRow label="Email" value={user.email ?? '—'} />
          <DataRow label="Auth provider" value={user.authProvider} />
          <DataRow label="Language" value={user.language.toUpperCase()} />
          <DataRow label="Avatar" value={user.avatarId} />
          <DataRow label="Created" value={formatDateTime(user.createdAt)} />
          <DataRow label="Updated" value={formatDateTime(user.updatedAt)} />
          <DataRow
            label="Last active"
            value={formatDateTime(user.lastActiveAt)}
          />
          {user.isBot && (
            <DataRow label="Bot skill tier" value={user.botSkillTier ?? '—'} />
          )}
        </PageSection>

        <PageSection
          title="Balances"
          bodySpacing="tight"
          testId="user-detail-balances"
          actions={
            <button
              type="button"
              onClick={() => setCurrencyOpen(true)}
              data-testid="user-detail-adjust-currency"
              className="text-xs rounded px-2 py-1 border border-slate-300 hover:bg-slate-50"
            >
              Adjust currency
            </button>
          }
        >
          <DataRow
            label="Coins"
            value={user.coins.toLocaleString()}
            testId="balance-coins"
          />
          <DataRow
            label="Gems"
            value={user.gems.toLocaleString()}
            testId="balance-gems"
          />
          <DataRow
            label="Energy"
            value={`${user.energy} / ${user.maxEnergy}`}
            testId="balance-energy"
          />
          <DataRow
            label="Energy last refill"
            value={formatDateTime(user.energyLastRefill)}
          />
          <DataRow
            label="Unlimited energy"
            value={
              user.unlimitedEnergyExpires
                ? `until ${formatDateTime(user.unlimitedEnergyExpires)}`
                : '—'
            }
          />
          <DataRow
            label="No-ads"
            value={
              user.noAdsExpires
                ? `until ${formatDateTime(user.noAdsExpires)}`
                : '—'
            }
          />
        </PageSection>
      </div>

      {/* Preferences -------------------------------------------------- */}
      <PageSection title="Preferences" testId="user-detail-preferences">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <BoolPill label="Sound" enabled={user.soundEnabled} />
          <BoolPill label="Music" enabled={user.musicEnabled} />
          <BoolPill label="Notifications" enabled={user.notificationsEnabled} />
        </div>
      </PageSection>

      {/* Stats -------------------------------------------------------- */}
      <PageSection title="Stats" testId="user-detail-stats">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCell label="Matches" value={user.totalMatches} />
          <StatCell label="Wins" value={user.totalWins} />
          <StatCell label="Losses" value={user.totalLosses} />
          <StatCell label="Correct answers" value={user.totalCorrectAnswers} />
        </div>
      </PageSection>

      {/* Recent matches ----------------------------------------------- */}
      <PageSection
        title="Recent matches"
        inset={false}
        testId="user-detail-recent-matches"
      >
        <RecentMatchesTable userId={user.id} rows={user.recentMatches} />
      </PageSection>

      {/* Devices -------------------------------------------------------- */}
      <PageSection
        title="Devices"
        inset={false}
        testId="user-detail-devices"
      >
        <DevicesTable rows={user.devices ?? []} />
      </PageSection>

      {/* Transactions ------------------------------------------------- */}
      <PageSection
        title="Transactions"
        inset={false}
        testId="user-detail-transactions"
      >
        <TransactionsTable
          rows={transactions?.data ?? []}
          loading={transactions == null}
        />
        {transactions && transactions.total > transactions.limit && (
          <div className="flex items-center justify-between mt-3 text-sm text-slate-600">
            <span data-testid="user-transactions-status">
              Page {transactions.page} · {transactions.total.toLocaleString()}{' '}
              total
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() =>
                  handleTransactionsPageChange(Math.max(1, transactions.page - 1))
                }
                disabled={transactions.page <= 1}
                data-testid="user-transactions-prev"
                className="text-xs rounded px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  handleTransactionsPageChange(transactions.page + 1)
                }
                disabled={!transactions.hasMore}
                data-testid="user-transactions-next"
                className="text-xs rounded px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </PageSection>

      {/* Audit preview ------------------------------------------------ */}
      <PageSection
        title="Recent audit entries"
        inset={false}
        testId="user-detail-audit"
        actions={
          <Link
            to={`/audit?targetType=user&targetId=${encodeURIComponent(user.id)}`}
            className="text-xs text-slate-600 hover:text-slate-900 hover:underline"
            data-testid="user-detail-audit-see-all"
          >
            See all in Audit Log →
          </Link>
        }
      >
        <AuditPreviewTable rows={audit?.data ?? []} loading={audit == null} />
      </PageSection>

      {/* Ban modal (reason required) --------------------------------- */}
      <ReasonedConfirmModal
        open={confirmKind === 'ban'}
        title="Ban this user?"
        description="They will be signed out of all sessions and can no longer log in. You can unban them later."
        confirmLabel={confirmBusy ? 'Working…' : 'Ban user'}
        tone="danger"
        busy={confirmBusy}
        testId="ban-modal"
        reasonPlaceholder="e.g. Repeated chat abuse in match #XYZ"
        onCancel={closeConfirm}
        onConfirm={handleBanOrUnban}
      />

      <ReasonedConfirmModal
        open={confirmKind === 'unban'}
        title="Unban this user?"
        description="They will be able to log in again. Their token version is not changed — existing banned sessions stay invalidated."
        confirmLabel={confirmBusy ? 'Working…' : 'Unban user'}
        tone="safe"
        busy={confirmBusy}
        testId="unban-modal"
        reasonPlaceholder="e.g. Appeal upheld after review of match #XYZ"
        onCancel={closeConfirm}
        onConfirm={handleBanOrUnban}
      />

      {/* Currency modal ----------------------------------------------- */}
      <CurrencyModal
        open={currencyOpen}
        onClose={() => setCurrencyOpen(false)}
        user={{
          id: user.id,
          coins: user.coins,
          gems: user.gems,
          energy: user.energy,
          maxEnergy: user.maxEnergy,
        }}
        onSuccess={handleCurrencySuccess}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCell({ label, value }: { label: string; value: number }) {
  return (
    <Card tone="muted" padding="sm">
      <div className="text-xs text-slate-500 uppercase tracking-wide">
        {label}
      </div>
      <div className="text-lg font-semibold tabular-nums text-slate-900">
        {value.toLocaleString()}
      </div>
    </Card>
  );
}

function RecentMatchesTable({
  userId,
  rows,
}: {
  userId: string;
  rows: readonly AdminUserRecentMatch[];
}) {
  const columns: DataTableColumn<AdminUserRecentMatch>[] = useMemo(
    () => [
      {
        key: 'mode',
        header: 'Mode',
        render: (m) => <Pill variant="neutral">{m.mode}</Pill>,
      },
      {
        key: 'opponent',
        header: 'Opponent',
        render: (m) => (
          <Link
            to={`/users/${encodeURIComponent(m.opponentId)}`}
            className="text-slate-700 hover:text-slate-900 hover:underline"
          >
            {m.opponentName}{' '}
            <span className="text-slate-400 text-xs">({m.opponentId})</span>
          </Link>
        ),
      },
      {
        key: 'match',
        header: 'Match',
        render: (m) => (
          <Link
            to={`/matches/${encodeURIComponent(m.id)}`}
            className="font-mono text-xs text-slate-600 hover:text-slate-900 hover:underline"
          >
            {m.id.slice(0, 6)}…
          </Link>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (m) => <Pill variant="neutral">{m.status}</Pill>,
      },
      {
        key: 'score',
        header: 'Score',
        render: (m) => (
          <span className="text-slate-700 font-semibold tabular-nums">
            {m.yourScore} – {m.opponentScore}
          </span>
        ),
      },
      {
        key: 'outcome',
        header: 'Outcome',
        render: (m) => {
          // N-seat table (#679): placement, not winner/loser.
          if (m.playerCount && m.playerCount > 2) {
            if (m.placement == null) return <Pill variant="neutral">—</Pill>;
            return (
              <Pill variant={m.placement === 1 ? 'success' : 'neutral'}>
                #{m.placement} of {m.playerCount}
              </Pill>
            );
          }
          if (m.winnerId === null) {
            return <Pill variant="neutral">—</Pill>;
          }
          const won = m.winnerId === userId;
          return (
            <Pill variant={won ? 'success' : 'danger'}>
              {won ? 'Winner' : 'Loser'}
            </Pill>
          );
        },
      },
      {
        key: 'startedAt',
        header: 'Started',
        render: (m) => (
          <span className="text-xs text-slate-500">
            {formatDateTime(m.startedAt)}
          </span>
        ),
      },
      {
        key: 'endedAt',
        header: 'Ended',
        render: (m) => (
          <span className="text-xs text-slate-500">
            {m.endedAt ? formatDateTime(m.endedAt) : '—'}
          </span>
        ),
      },
    ],
    [userId],
  );

  return (
    <DataTable<AdminUserRecentMatch>
      columns={columns}
      rows={rows}
      rowKey={(m) => m.id}
      empty="No recent matches."
      testId="user-recent-matches-table"
    />
  );
}

/**
 * Devices this account has signed in / played from (#device-tracking).
 *
 * The device HASH is a link into the users list rather than plain text: that
 * search resolves it to every account which has used the device, which is the
 * whole reason an operator opens this table. The raw device id is never
 * stored or rendered — see `AdminUserDevice.deviceIdHash`. `sharedWithAccountCount`
 * is flagged only when it exceeds 1, so the ordinary single-account case
 * stays quiet.
 *
 * The empty state says "not reported" rather than "none". An account whose
 * client predates the device headers legitimately has no rows, and reading
 * that as "this user has no devices" would be wrong in the exact case an
 * operator is investigating.
 */
function DevicesTable({ rows }: { rows: readonly AdminUserDevice[] }) {
  const columns: DataTableColumn<AdminUserDevice>[] = useMemo(
    () => [
      {
        key: 'deviceIdHash',
        header: 'Device',
        render: (d) => (
          // The HASH, never the raw device id: `POST /auth/guest`
          // authenticates on the raw id alone, so rendering it here — into a
          // URL, a title attribute and browser history — would hand every
          // operator an account-takeover primitive against every guest.
          <Link
            to={`/users?q=${encodeURIComponent(d.deviceIdHash)}`}
            className="font-mono text-xs text-slate-600 hover:text-slate-900 hover:underline"
            title={d.deviceIdHash}
          >
            {d.deviceIdHash.slice(0, 8)}…
          </Link>
        ),
      },
      {
        key: 'platform',
        header: 'Platform',
        render: (d) => (
          <Pill variant="neutral">
            {d.platform === 'unknown' ? 'not reported' : d.platform}
          </Pill>
        ),
      },
      {
        key: 'versions',
        header: 'App / OS',
        render: (d) => (
          <span className="text-xs text-slate-600 tabular-nums">
            {d.appVersion ?? '—'} / {d.osVersion ?? '—'}
          </span>
        ),
      },
      {
        key: 'lastAuthProvider',
        header: 'Signed in via',
        render: (d) => (
          <span className="text-xs text-slate-600">{d.lastAuthProvider ?? '—'}</span>
        ),
      },
      {
        key: 'loginCount',
        header: 'Logins',
        render: (d) => (
          <span className="text-slate-700 tabular-nums">{d.loginCount}</span>
        ),
      },
      {
        key: 'shared',
        header: 'Accounts',
        render: (d) =>
          d.sharedWithAccountCount > 1 ? (
            <Pill variant="warning">{d.sharedWithAccountCount} accounts</Pill>
          ) : (
            <Pill variant="neutral">1</Pill>
          ),
      },
      {
        key: 'lastIp',
        header: 'Last IP',
        render: (d) => (
          <span className="font-mono text-xs text-slate-500">{d.lastIp ?? '—'}</span>
        ),
      },
      {
        key: 'firstSeenAt',
        header: 'First seen',
        render: (d) => (
          <span className="text-xs text-slate-500">{formatDateTime(d.firstSeenAt)}</span>
        ),
      },
      {
        key: 'lastSeenAt',
        header: 'Last seen',
        render: (d) => (
          <span className="text-xs text-slate-500">{formatDateTime(d.lastSeenAt)}</span>
        ),
      },
    ],
    [],
  );

  return (
    <DataTable<AdminUserDevice>
      columns={columns}
      rows={rows}
      rowKey={(d) => d.deviceIdHash}
      empty="No devices reported for this account."
      testId="user-devices-table"
    />
  );
}

function AuditPreviewTable({
  rows,
  loading,
}: {
  rows: readonly AuditLogEntry[];
  loading: boolean;
}) {
  const columns: DataTableColumn<AuditLogEntry>[] = useMemo(
    () => [
      {
        key: 'action',
        header: 'Action',
        render: (e) => (
          <span className="font-mono text-xs text-slate-900">{e.action}</span>
        ),
      },
      {
        key: 'actorId',
        header: 'Actor',
        render: (e) => (
          <Link
            to={`/admin-users/${encodeURIComponent(e.actorId)}`}
            className="font-mono text-xs text-slate-700 hover:text-slate-900 hover:underline"
            title={e.actorId}
          >
            {e.actorId}
          </Link>
        ),
      },
      {
        key: 'diff',
        header: 'Change',
        render: (e) => <DiffPreview before={e.before} after={e.after} />,
      },
      {
        key: 'createdAt',
        header: 'When',
        render: (e) => (
          <span className="text-xs text-slate-500">
            {formatDateTime(e.createdAt)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <DataTable<AuditLogEntry>
      columns={columns}
      rows={rows}
      rowKey={(e) => e.id}
      loading={loading}
      empty="No recent audit entries for this user."
      testId="user-audit-preview-table"
    />
  );
}

/**
 * Compact before→after diff preview. We stringify both sides and truncate —
 * a full pretty-printed diff is out of scope for the preview table and
 * belongs on the dedicated audit viewer (tracked elsewhere in epic #281).
 */
function DiffPreview({
  before,
  after,
}: {
  before: unknown;
  after: unknown;
}) {
  const beforeStr = stringifyCompact(before);
  const afterStr = stringifyCompact(after);
  return (
    <div className="text-xs font-mono text-slate-600 max-w-[24rem] truncate">
      <span className="text-red-700" title={beforeStr}>
        {beforeStr}
      </span>
      <span className="mx-1 text-slate-400">→</span>
      <span className="text-emerald-700" title={afterStr}>
        {afterStr}
      </span>
    </div>
  );
}

function stringifyCompact(v: unknown): string {
  if (v === null) return 'null';
  if (v === undefined) return '—';
  try {
    const s = JSON.stringify(v);
    if (s.length > 80) return s.slice(0, 77) + '…';
    return s;
  } catch {
    return String(v);
  }
}

function BoolPill({ label, enabled }: { label: string; enabled: boolean }) {
  return (
    <Card tone="muted" padding="sm" className="flex items-center justify-between">
      <span className="text-xs text-slate-500 uppercase tracking-wide">
        {label}
      </span>
      <Pill variant={enabled ? 'success' : 'neutral'}>
        {enabled ? 'On' : 'Off'}
      </Pill>
    </Card>
  );
}

/**
 * Well-known metadata keys the transaction logger attaches. When present,
 * these render as linked / formatted chips in the "Context" column so admins
 * can pivot to the related match, read the grant reason, or read the IAP
 * product without parsing a JSON blob. Everything else falls through to a
 * truncated JSON string — the raw fallback is still there for unknown keys.
 *
 * UX-researcher audit finding #2: a truncated JSON blob hid "why" at the
 * exact moment an admin was about to adjust currency again. Surfacing the
 * reason + matchId as first-class columns removes that hunt.
 */
function metadataContext(
  metadata: Record<string, unknown> | null,
): ReactNode {
  if (!metadata) return <span className="text-slate-400">—</span>;
  const chips: ReactNode[] = [];
  const matchId = typeof metadata.matchId === 'string' ? metadata.matchId : null;
  const reason = typeof metadata.reason === 'string' ? metadata.reason : null;
  const productId =
    typeof metadata.productId === 'string' ? metadata.productId : null;
  const adNetwork =
    typeof metadata.adNetwork === 'string' ? metadata.adNetwork : null;
  const grantedByAdminId =
    typeof metadata.grantedByAdminId === 'string'
      ? metadata.grantedByAdminId
      : null;
  if (matchId) {
    chips.push(
      <Link
        key="match"
        to={`/matches/${encodeURIComponent(matchId)}`}
        className="text-xs font-mono bg-slate-100 text-slate-700 rounded px-2 py-0.5 hover:text-slate-900 hover:underline"
      >
        match:{matchId.slice(0, 6)}…
      </Link>,
    );
  }
  if (grantedByAdminId) {
    chips.push(
      <Link
        key="admin"
        to={`/admin-users/${encodeURIComponent(grantedByAdminId)}`}
        className="text-xs font-mono bg-slate-100 text-slate-700 rounded px-2 py-0.5 hover:text-slate-900 hover:underline"
      >
        admin:{grantedByAdminId.slice(0, 6)}…
      </Link>,
    );
  }
  if (productId) {
    chips.push(
      <span
        key="product"
        className="text-xs font-mono bg-slate-100 text-slate-700 rounded px-2 py-0.5"
      >
        iap:{productId}
      </span>,
    );
  }
  if (adNetwork) {
    chips.push(
      <span
        key="ad"
        className="text-xs font-mono bg-slate-100 text-slate-700 rounded px-2 py-0.5"
      >
        ad:{adNetwork}
      </span>,
    );
  }
  if (reason) {
    chips.push(
      <span
        key="reason"
        className="text-xs text-slate-700 max-w-[20rem] truncate"
        title={reason}
      >
        “{reason}”
      </span>,
    );
  }
  if (chips.length > 0) {
    return <div className="flex flex-wrap items-center gap-1.5">{chips}</div>;
  }
  // Fallback: no well-known keys matched — surface raw JSON compact form.
  const raw = JSON.stringify(metadata);
  return (
    <span
      className="font-mono text-xs text-slate-500 max-w-[20rem] truncate block"
      title={raw}
    >
      {raw}
    </span>
  );
}

function TransactionsTable({
  rows,
  loading,
}: {
  rows: readonly AdminUserTransaction[];
  loading: boolean;
}) {
  const columns: DataTableColumn<AdminUserTransaction>[] = useMemo(
    () => [
      {
        key: 'createdAt',
        header: 'When',
        render: (t) => (
          <span className="text-xs text-slate-500 whitespace-nowrap">
            {formatDateTime(t.createdAt)}
          </span>
        ),
      },
      {
        key: 'type',
        header: 'Type',
        render: (t) => (
          <span className="font-mono text-xs text-slate-900">{t.type}</span>
        ),
      },
      {
        key: 'currency',
        header: 'Currency',
        render: (t) => <Pill variant="neutral">{t.currency}</Pill>,
      },
      {
        key: 'amount',
        header: 'Amount',
        render: (t) => (
          <span
            className={
              'text-sm font-semibold tabular-nums ' +
              (t.amount >= 0 ? 'text-emerald-700' : 'text-red-700')
            }
          >
            {t.amount >= 0 ? '+' : ''}
            {t.amount.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'balanceAfter',
        header: 'Balance',
        render: (t) => (
          <span className="text-sm font-semibold tabular-nums text-slate-700">
            {t.balanceAfter.toLocaleString()}
          </span>
        ),
      },
      {
        key: 'metadata',
        header: 'Context',
        render: (t) => metadataContext(t.metadata),
      },
    ],
    [],
  );

  return (
    <DataTable<AdminUserTransaction>
      columns={columns}
      rows={rows}
      rowKey={(t) => t.id}
      loading={loading}
      empty="No transactions yet."
      testId="user-transactions-table"
    />
  );
}
